import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Menu, X, Mail, Linkedin, Phone, Download, FileText, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Whitepaper registry — keep in sync with backend WHITEPAPERS dict in server.py
// To add a new whitepaper:
//   1. Drop the PDF in /app/frontend/public/whitepapers/
//   2. Add an entry below
//   3. Add the same slug + metadata to WHITEPAPERS in /app/backend/server.py
const WHITEPAPERS = [
  {
    slug: "tax-reform",
    title: "Navigating Brazil's Tax Reform",
    subtitle: "Risks, Opportunities and Compliance for American Companies",
    description:
      "A comprehensive executive briefing on Brazil's landmark tax overhaul — what it means for U.S. exporters, multinational corporations, and cross-border investors operating in or entering the Brazilian market.",
    descriptionPt:
      "Um briefing executivo abrangente sobre a reforma tributária histórica do Brasil — o que ela significa para exportadores americanos, multinacionais e investidores transfronteiriços que operam ou estão entrando no mercado brasileiro.",
  },
  {
    slug: "mercosur-eu",
    title: "The Mercosur–EU Agreement",
    subtitle: "How American Exports to Brazil Are Being Repositioned",
    description:
      "A comprehensive executive briefing on the structural impact of the Mercosur–European Union trade agreement on U.S. exporters, multinational corporations, and cross-border operators active in the Brazilian market — and the compliance and sourcing implications that follow.",
    descriptionPt:
      "Um briefing executivo abrangente sobre o impacto estrutural do acordo comercial Mercosul–União Europeia para exportadores americanos, multinacionais e operadores transfronteiriços atuantes no mercado brasileiro — e as implicações de compliance e sourcing que dele decorrem.",
  },
  {
    slug: "rare-earths",
    title: "Rare Earths in the Americas",
    subtitle: "The New Strategic Race Between the U.S. and Brazil",
    description:
      "A comprehensive executive briefing on the U.S.–Brazil rare earths partnership and the geopolitical race shaping a new value chain under construction — opportunities for companies across mining, logistics, technology, finance, foreign trade compliance, and professional training in the Western Hemisphere.",
    descriptionPt:
      "Um briefing executivo abrangente sobre a parceria EUA–Brasil em terras raras e a corrida geopolítica que molda uma nova cadeia de valor em construção — oportunidades para empresas em mineração, logística, tecnologia, finanças, compliance de comércio exterior e formação profissional no Hemisfério Ocidental.",
  },
  {
    slug: "lpco-duimp",
    title: "LPCO in DUIMP: Import Licensing, Anuent Agencies, and the New Customs Gateway",
    subtitle:
      "A practical guide for U.S. exporters and compliance teams on Brazil's new import-licensing framework — LPCOs, anuent agencies, and the transition from legacy LI to the Portal Único Siscomex.",
    description:
      "A practical executive guide on Brazil's new import-licensing framework: how LPCOs (Licenses, Permits, Certificates and Other Documents) operate inside DUIMP, the role of the anuent agencies, and the operational shift from the legacy LI regime to the Portal Único Siscomex — with the compliance, timing, and documentation implications for U.S. exporters and their customs partners.",
    descriptionPt:
      "Um guia executivo prático sobre o novo arcabouço de licenciamento de importação do Brasil: como os LPCOs (Licenças, Permissões, Certificados e Outros Documentos) operam dentro da DUIMP, o papel dos órgãos anuentes e a transição operacional do regime legado de LI para o Portal Único Siscomex — com as implicações de compliance, prazos e documentação para exportadores americanos e seus parceiros aduaneiros.",
  },
];

function WhitepaperCard({ wp }) {
  const [data, setData] = useState({ name: "", email: "", company: "" });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setData({ ...data, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus({ type: "", message: "" });
    try {
      await axios.post(`${API}/whitepaper/request`, { ...data, whitepaper_slug: wp.slug });
      setStatus({
        type: "success",
        message:
          "Thank you! Check your inbox — the whitepaper download link is on its way. / Obrigado! Verifique seu e-mail — o link para download foi enviado.",
      });
      setData({ name: "", email: "", company: "" });
    } catch (err) {
      setStatus({
        type: "error",
        message: "Something went wrong. Please try again. / Algo deu errado. Por favor, tente novamente.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid={`whitepaper-card-${wp.slug}`}
      className="bg-btacc-blue rounded-lg shadow-2xl overflow-hidden grid lg:grid-cols-5"
    >
      {/* Left side: title + description */}
      <div className="lg:col-span-3 p-8 lg:p-10 text-white">
        <div className="inline-flex items-center bg-btacc-gold/20 text-btacc-gold px-3 py-1 rounded-full text-xs font-montserrat font-semibold tracking-wide uppercase mb-5">
          <FileText size={14} className="mr-2" />
          BTACC Whitepaper
        </div>
        <h3 className="text-2xl md:text-3xl font-montserrat font-bold mb-3 leading-tight">
          {wp.title}
        </h3>
        <p className="text-lg font-inter text-btacc-gold mb-5 italic">{wp.subtitle}</p>
        <p className="text-sm font-inter text-white/90 leading-relaxed mb-3">{wp.description}</p>
        <p className="text-xs font-inter text-white/60 italic">{wp.descriptionPt}</p>
      </div>

      {/* Right side: form */}
      <div className="lg:col-span-2 bg-white p-7 lg:p-8 text-btacc-graphite">
        <h4 className="text-xl font-montserrat font-bold text-btacc-blue mb-1">Get the Whitepaper</h4>
        <p className="text-xs font-inter text-btacc-secondary italic mb-5">
          Receba o whitepaper no seu e-mail
        </p>

        <form onSubmit={handleSubmit} data-testid={`whitepaper-form-${wp.slug}`} className="space-y-3">
          <input
            type="text"
            name="name"
            placeholder="Name / Nome *"
            required
            value={data.name}
            onChange={handleChange}
            className="w-full px-3.5 py-2.5 border border-gray-300 rounded-md font-inter text-sm focus:outline-none focus:ring-2 focus:ring-btacc-gold focus:border-transparent"
            data-testid={`wp-${wp.slug}-name`}
          />
          <input
            type="email"
            name="email"
            placeholder="Corporate Email / E-mail Corporativo *"
            required
            value={data.email}
            onChange={handleChange}
            className="w-full px-3.5 py-2.5 border border-gray-300 rounded-md font-inter text-sm focus:outline-none focus:ring-2 focus:ring-btacc-gold focus:border-transparent"
            data-testid={`wp-${wp.slug}-email`}
          />
          <input
            type="text"
            name="company"
            placeholder="Company / Empresa *"
            required
            value={data.company}
            onChange={handleChange}
            className="w-full px-3.5 py-2.5 border border-gray-300 rounded-md font-inter text-sm focus:outline-none focus:ring-2 focus:ring-btacc-gold focus:border-transparent"
            data-testid={`wp-${wp.slug}-company`}
          />
          <button
            type="submit"
            disabled={submitting}
            data-testid={`wp-${wp.slug}-submit`}
            className="w-full bg-btacc-gold text-btacc-blue px-5 py-3 rounded-md font-montserrat font-bold text-sm hover:bg-btacc-blue hover:text-white transition flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Download size={16} className="mr-2" />
            {submitting ? "Sending…" : "Download"}
          </button>

          {status.message && (
            <div
              data-testid={`wp-${wp.slug}-status`}
              className={`text-xs font-inter p-3 rounded-md ${
                status.type === "success"
                  ? "bg-green-50 text-green-800 border border-green-200"
                  : "bg-red-50 text-red-800 border border-red-200"
              }`}
            >
              {status.message}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default function Whitepapers() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="App bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-btacc-blue z-50 shadow-lg" data-testid="header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex-shrink-0" data-testid="logo">
              <h1 className="text-3xl font-montserrat font-bold text-white">
                BT<span className="text-btacc-gold">ACC</span>
              </h1>
              <p className="text-xs text-btacc-light font-inter -mt-1">
                Brazil Tax Advisory &amp; Compliance Center
              </p>
            </Link>

            <nav className="hidden lg:flex items-center space-x-8" data-testid="desktop-nav">
              <Link to="/" className="text-sm font-inter text-white hover:text-btacc-gold transition">Home</Link>
              <Link to="/whitepapers" className="text-sm font-inter text-btacc-gold hover:text-white transition font-semibold">Whitepapers</Link>
              <Link to="/#contact" className="bg-btacc-gold text-btacc-blue px-6 py-2.5 rounded-md font-montserrat font-semibold text-sm hover:bg-white transition" data-testid="book-consultation-btn">
                Book a Consultation
              </Link>
            </nav>

            <button
              className="lg:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden bg-btacc-secondary border-t border-btacc-blue" data-testid="mobile-nav">
            <div className="px-4 py-4 space-y-3">
              <Link to="/" className="block w-full text-left py-2 text-white hover:text-btacc-gold">Home</Link>
              <Link to="/whitepapers" className="block w-full text-left py-2 text-btacc-gold hover:text-white font-semibold">Whitepapers</Link>
              <Link to="/#contact" className="block w-full text-center mt-2 bg-btacc-gold text-btacc-blue px-6 py-2.5 rounded-md font-montserrat font-semibold">Book a Consultation</Link>
            </div>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="pt-32 pb-12 bg-btacc-light" data-testid="whitepapers-hero">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center bg-btacc-blue/10 text-btacc-blue px-4 py-1.5 rounded-full text-xs font-montserrat font-semibold tracking-wide uppercase mb-6">
            <FileText size={14} className="mr-2" />
            BTACC Library
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-montserrat font-bold text-btacc-blue mb-5 leading-tight">
            Whitepapers &amp; Executive Briefings
          </h1>
          <p className="text-lg font-inter text-btacc-graphite mb-2">
            Strategic intelligence on Brazilian tax, customs, labor and compliance — written for American business leaders.
          </p>
          <p className="text-sm font-inter text-btacc-secondary italic">
            Inteligência estratégica sobre tributação, alfândega, trabalho e compliance brasileiros — escrito para líderes empresariais americanos.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm font-inter text-btacc-secondary">
            <span className="flex items-center"><CheckCircle2 size={16} className="text-btacc-gold mr-2" />Free downloads</span>
            <span className="flex items-center"><CheckCircle2 size={16} className="text-btacc-gold mr-2" />Executive-level depth</span>
            <span className="flex items-center"><CheckCircle2 size={16} className="text-btacc-gold mr-2" />Updated regularly</span>
          </div>
        </div>
      </section>

      {/* Whitepaper cards */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10" data-testid="whitepapers-list">
          {WHITEPAPERS.map((wp) => (
            <WhitepaperCard key={wp.slug} wp={wp} />
          ))}

          {WHITEPAPERS.length === 0 && (
            <div className="text-center py-16 text-btacc-secondary font-inter italic">
              New whitepapers coming soon. / Novos whitepapers em breve.
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-btacc-blue text-white py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-montserrat font-bold mb-3">
                BT<span className="text-btacc-gold">ACC</span>
              </h3>
              <p className="text-sm font-inter text-btacc-light leading-relaxed">
                U.S.-based center specialized in Brazilian tax, customs, labor and compliance legislation.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-montserrat font-bold uppercase tracking-wide mb-3 text-btacc-gold">Contact</h4>
              <p className="text-sm font-inter text-btacc-light flex items-start mb-2">
                <Mail size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                contact@btacc.info
              </p>
              <p className="text-sm font-inter text-btacc-light flex items-start mb-2">
                <Phone size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                +1 (844) 595-2060
              </p>
              <p className="text-sm font-inter text-btacc-light leading-relaxed">
                30 N Gould St Ste R<br />Sheridan, WY 82801, USA
              </p>
            </div>
            <div>
              <h4 className="text-sm font-montserrat font-bold uppercase tracking-wide mb-3 text-btacc-gold">Explore</h4>
              <ul className="space-y-2 text-sm font-inter text-btacc-light">
                <li><Link to="/" className="hover:text-btacc-gold transition">Home</Link></li>
                <li><Link to="/whitepapers" className="hover:text-btacc-gold transition">Whitepapers</Link></li>
                <li><Link to="/careers" className="hover:text-btacc-gold transition">Careers</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-btacc-secondary pt-6 flex flex-col md:flex-row justify-between items-center text-xs font-inter text-btacc-light">
            <p>© {new Date().getFullYear()} BTACC — Brazil Tax Advisory &amp; Compliance Center. All rights reserved.</p>
            <a href="https://www.linkedin.com/company/btacc" target="_blank" rel="noopener noreferrer" className="hover:text-btacc-gold transition mt-2 md:mt-0 flex items-center">
              <Linkedin size={16} className="mr-2" />LinkedIn
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
